import type { FetchData } from '@hooks/useDataFetcher';
import { capitalizeFirstChar } from '@shared/helpers';
import { useCallback, useState } from 'react';
import type { IFinancingProduct } from 'types/custom';

import type { SnackbarActions } from './useSnackbar';

interface ICollection<T> {
  path: string;
  list: Array<T>;
  loading: boolean;
}

interface IState {
  financingProducts: ICollection<IFinancingProduct>;
}

export type UseFinancingProducts = IState & {
  loading: boolean;
  getFinancingProducts: () => Promise<IFinancingProduct[]>;
};

const API_PREFIX = '/api/gpe-config';

export const useFinancingProducts = (
  makeRequest: FetchData,
  snackbar: SnackbarActions,
): UseFinancingProducts => {
  const [fetchCount, setFetchCount] = useState(0);
  const [state, setState] = useState<IState>({
    financingProducts: { list: [], loading: false, path: '/' },
  });

  const GET = useCallback(
    async <T = any>(path: string, key?: keyof IState): Promise<T[]> => {
      try {
        // If no key provided, just make the API call without state management
        if (!key) {
          setFetchCount((count) => count + 1);
          const res = await makeRequest<any>(`${API_PREFIX}/${path}`);
          setFetchCount((count) => count - 1);
          return res?.data ?? [];
        }

        setState((state) => {
          const val = state[key];

          if (val.path === path && val.list.length > 0) {
            return state;
          }

          return { ...state, [key]: { ...val, loading: true } };
        });

        // Get current state value for the cache check
        const currentVal = await new Promise<ICollection<T>>((resolve) => {
          setState((prevState) => {
            resolve(prevState[key] as ICollection<T>);
            return prevState;
          });
        });

        if (currentVal.path === path && currentVal.list.length > 0) {
          return currentVal.list as Array<T>;
        }

        const res = await makeRequest<any>(`${API_PREFIX}/${path}`);
        const list = res?.data?.[key] ?? [];

        setState((state) => ({
          ...state,
          [key]: { list, path, loading: false },
        }));

        return list;
      } catch (err) {
        if (key) {
          setState((state) => ({
            ...state,
            [key]: { ...state[key], loading: false },
          }));
          snackbar.error(`Failed to fetch ${capitalizeFirstChar(key, true)}.`);
        } else {
          setFetchCount((count) => count - 1);
        }

        throw err;
      }
    },
    [makeRequest, snackbar],
  );

  return {
    ...state,
    loading: fetchCount > 0,
    getFinancingProducts: () => GET('financing-products', 'financingProducts'),
  };
};
